import { Badge } from "@/components/ui/badge";
import { cn, isPriorityOverdue } from "@/lib/utils";
import { Priority, User } from "@/types";
import { ArrowUpRight, Lock, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { buildBlockedNote } from "./buildBlockedNote";
import { PriorityStatusTooltip } from "./PriorityStatusTooltip";
import { canAccessPriority } from "./priority-access";
import { getPriorityStatusMeta, priorityTypeBadgeClass, priorityTypeAccentClass } from "./priorityCardTokens";
import { PriorityPeopleRow } from "./PriorityPeopleRow";
import { PppChips } from "./PppChips";

interface PriorityCardProps {
  priority: Priority;
  onView?: (priority: Priority) => void;
  onEdit?: (priority: Priority) => void;
  onDelete?: (priority: Priority) => void;
  currentUser?: User | null;
  isAdmin?: boolean;
  /** PPP already named by the surrounding group header. */
  omitPppPid?: string;
}

export const PriorityCard = ({ priority, onView, onEdit, onDelete, currentUser, isAdmin, omitPppPid }: PriorityCardProps) => {
  const { canViewOkr } = useAuth();
  const status = getPriorityStatusMeta(priority.status);
  const StatusIcon = status.icon;
  const canDelete = isAdmin || priority?.isCreator;
  const isOverdue = priority.status !== "done" && isPriorityOverdue(priority.weekNumber, priority.weekYear);
  const blockedNote = buildBlockedNote(priority);

  if (!canAccessPriority(priority, currentUser?.pid, canViewOkr)) {
    return (
      <div className="group relative overflow-hidden rounded-[12px] border-[0.5px] border-border bg-card opacity-60">
        <div className="flex items-center gap-3 px-4 py-3">
          <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Confidential priority</span>
          <span className="ml-auto text-xs font-mono text-muted-foreground/60">{priority.code}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => onEdit?.(priority)}
      className={cn(
        "group relative cursor-pointer overflow-hidden rounded-[12px] border-[0.5px] border-border bg-card transition-all duration-200 hover:shadow-sm",
        priority.status === "focus" && "border-violet-200/60 dark:border-violet-800/60",
        isOverdue && "border-destructive/30",
        priority.status === "blocked" && "border-red-200 bg-red-50/35",
        priority.status === "done" && "opacity-75",
      )}
    >
      {/* Left accent bar by type */}
      <div className={cn(
        "absolute left-0 inset-y-0 w-[3px]",
        priorityTypeAccentClass[priority.type],
      )} />

      <div
        className={cn(
          "flex items-center gap-2 border-b-[0.5px] border-border pl-5 pr-4 py-3",
          priority.status === "blocked" && "border-red-200",
        )}
      >
        <div className="min-w-0 flex-1">
          <h3
            title={`${priority.code}: ${priority.name}`}
            className={cn(
              "line-clamp-2 min-w-0 text-[15px] font-medium leading-snug text-foreground",
              priority.status === "blocked" && "text-red-800 dark:text-red-200",
            )}
          >
            {priority.code}
            <span className="text-muted-foreground/45">: </span>
            {priority.name}
          </h3>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          {priority.isConfidential || priority.inheritsConfidential ? (
            <span title="Confidential" className="flex shrink-0 items-center">
              <Lock className="h-3.5 w-3.5 text-muted-foreground/60" />
            </span>
          ) : null}
          <PppChips ppps={priority.ppps} isCandidate={priority.isCandidate} omitPid={omitPppPid} max={2} />
          <Badge
            variant="outline"
            className={cn(
              "h-6 shrink-0 rounded border px-1.5 text-[9px] font-bold uppercase tracking-wide",
              priorityTypeBadgeClass[priority.type],
            )}
          >
            {priority.type}
          </Badge>
          <PriorityStatusTooltip status={priority.status} className="shrink-0">
            <Badge
              variant="outline"
              className={cn("h-6 shrink-0 gap-1 rounded-full border px-2.5 text-[11px] font-semibold", status.className)}
            >
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </Badge>
          </PriorityStatusTooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44 rounded-lg">
              {onView ? (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onView(priority);
                  }}
                >
                  <ArrowUpRight className="mr-2 h-4 w-4" />
                  Open
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(priority);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              {canDelete ? (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(priority);
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="pl-5 pr-4 pb-1 pt-0">
        <PriorityPeopleRow priority={priority} maxVisibleAssignees={5} embedInCard />
      </div>

      {blockedNote ? (
        <div
          className="border-t-[0.5px] border-red-200 bg-red-50/90 px-4 py-2 dark:bg-red-950/30"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-xs leading-snug text-red-800 dark:text-red-200">{blockedNote}</p>
        </div>
      ) : null}
    </div>
  );
};
